// pattern: Imperative Shell
// App entry point — hash router + view mounting

import { parseHash } from './router.ts';
import { clearElement, requireElement, createElement } from './dom.ts';
import { renderLanding } from '../components/landing-view.ts';
import { renderDashboard } from '../components/dashboard-view.ts';
import { renderUpload } from '../components/upload-view.ts';
import { renderEditor } from '../components/editor-view.ts';
import { renderQuizView } from '../components/quiz-view.ts';
import '../styles/flashcard.css';

type CleanupFn = (() => void) | undefined;

let currentCleanup: CleanupFn;

function renderRoute(): void {
  const app = requireElement<HTMLDivElement>('app');
  const route = parseHash(window.location.hash);

  // Cleanup previous view
  if (currentCleanup) {
    currentCleanup();
    currentCleanup = undefined;
  }
  clearElement(app);

  // Set context for CSS scoping — student views get playful styling
  app.dataset['context'] = route.type === 'quiz' ? 'student' : 'teacher';

  switch (route.type) {
    case 'landing':
      currentCleanup = renderLanding(app);
      break;
    case 'dashboard':
      currentCleanup = renderDashboard(app, route.dashboardId);
      break;
    case 'upload':
      currentCleanup = renderUpload(app, route.dashboardId);
      break;
    case 'editor':
      currentCleanup = renderEditor(app, route.dashboardId, route.quizId);
      break;
    case 'quiz':
      currentCleanup = renderQuizView(app, route.quizId);
      break;
    case 'not-found': {
      const notFoundCard = createElement('div', { className: 'card text-center not-found-card' });
      const notFoundHeading = createElement('h2', { textContent: 'Page Not Found' });
      const notFoundMsg = createElement('p', {
        className: 'text-secondary not-found-msg',
        textContent: "The page you're looking for doesn't exist.",
      });
      const homeLink = createElement('a', {
        className: 'btn btn-primary',
        textContent: 'Go Home',
      });
      homeLink.href = '#/';
      notFoundCard.appendChild(notFoundHeading);
      notFoundCard.appendChild(notFoundMsg);
      notFoundCard.appendChild(homeLink);
      app.appendChild(notFoundCard);
      break;
    }
  }
}

function init(): void {
  window.addEventListener('hashchange', renderRoute);

  // Handle ?q= parameter from OG share links
  const params = new URLSearchParams(window.location.search);
  const quizParam = params.get('q');
  if (quizParam) {
    // Clean up the URL and navigate to hash route
    history.replaceState(null, '', window.location.pathname);
    window.location.hash = `/q/${quizParam}`;
    return; // hashchange event will trigger renderRoute
  }

  renderRoute();
}

document.addEventListener('DOMContentLoaded', init);
